import { useEffect, useState } from "react";
import { CaptureDetail as CaptureDetailData, CaptureStatus } from "../../../types";
import { ApiError, CaptureApi } from "../api";
import { relativeTime } from "../format";
import { href, navigate } from "../route";
import { LevelBadge } from "./LevelBadge";
import { Button, Panel, inputClass } from "./Shell";
import { Timeline } from "./Timeline";

const Row = ({ label, children }: { label: string; children: React.ReactNode }) =>
  children ? (
    <div className="flex gap-2 text-sm">
      <span className="w-24 shrink-0 text-neutral-500">{label}</span>
      <span className="min-w-0 break-all">{children}</span>
    </div>
  ) : null;

export const CaptureDetail = ({ id }: { id: string }) => {
  const [capture, setCapture] = useState<CaptureDetailData | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [amendText, setAmendText] = useState("");
  const [moveTo, setMoveTo] = useState("");

  const load = async () => {
    try {
      setCapture(await CaptureApi.get(id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Request failed");
    } finally {
      setBusy(false);
    }
  };

  if (error && !capture) return <p className="text-sm text-red-600">{error}</p>;
  if (!capture) return <p className="text-sm text-neutral-500">Loading…</p>;
  const status: CaptureStatus = capture.status ?? "open";
  const truncatedNote = capture.breadcrumbsTruncated
    ? `The client could not fit every breadcrumb in the first message${capture.breadcrumbsOmitted ? ` (${capture.breadcrumbsOmitted} sent separately)` : ""}; ${capture.amendments} amendment${capture.amendments === 1 ? "" : "s"} merged in.`
    : undefined;

  return (
    <div className="space-y-3">
      <Panel className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <LevelBadge level={capture.level} />
          <span>{capture.kind}</span>
          <span className="font-mono">{capture.source}</span>
          <span className="rounded bg-neutral-100 dark:bg-neutral-800 px-1">{capture.env}</span>
          {status !== "open" && <span className="italic">{status}</span>}
          {capture.floodSampled && <span className="rounded bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 px-1" title="Kept as a 1-in-N sample while the group was over the flood limit">flood sample</span>}
          {capture.suppressedBefore && <span className="rounded bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 px-1" title="The sender's client-side rate limit console-logged these instead of sending them, between the previous sent capture and this one">{capture.suppressedBefore} suppressed before this</span>}
          <span className="flex-1" />
          <span title={capture.receivedAt}>received {relativeTime(capture.receivedAt)}</span>
        </div>
        <h1 className="text-lg font-semibold break-words">
          {capture.error?.name && <span>{capture.error.name}: </span>}
          {capture.message}
        </h1>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
          <Row label="Happened">{new Date(capture.timestamp).toLocaleString()}</Row>
          <Row label="Received">{new Date(capture.receivedAt).toLocaleString()}</Row>
          <Row label="Runtime">{capture.runtime}</Row>
          <Row label="Lambda">{capture.lambdaName}</Row>
          <Row label="Location">{capture.location}</Row>
          <Row label="Request">{capture.requestId}</Row>
          <Row label="Release">{capture.release}</Row>
          <Row label="SDK copy">{capture.sdkVersion}</Row>
          <Row label="User">
            {capture.user && (
              <a className="hover:underline" href={href.captures({ userId: capture.user.id })}>
                {[capture.user.username, capture.user.email, capture.user.id].filter(Boolean).join(" · ")}
              </a>
            )}
          </Row>
          <Row label="Flow">{capture.flowId && <a className="hover:underline" href={href.captures({ flowId: capture.flowId })}>{capture.flowId}</a>}</Row>
          <Row label="Group"><a className="hover:underline" href={href.group(capture.groupId)}>{capture.groupId}</a></Row>
          <Row label="Fingerprint"><span className="font-mono text-xs">{capture.fingerprint}</span></Row>
          <Row label="Id"><span className="font-mono text-xs">{capture.id}</span></Row>
          <Row label="Tags">{capture.tags && Object.keys(capture.tags).length > 0 && Object.entries(capture.tags).map(([k, v]) => <span key={k} className="inline-block mr-1 rounded bg-neutral-100 dark:bg-neutral-800 px-1 text-xs">{k}={v}</span>)}</Row>
        </div>
        <div className="flex flex-wrap gap-2 items-center pt-1">
          <select className={inputClass} value={status} disabled={busy} onChange={(e) => act(() => CaptureApi.update({ id, status: e.target.value as CaptureStatus }))}>
            <option value="open">open</option>
            <option value="resolved">resolved</option>
            <option value="ignored">ignored</option>
          </select>
          <input className={`${inputClass} w-72`} placeholder="move to group id…" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} />
          <Button disabled={busy || !moveTo.trim()} onClick={() => act(() => CaptureApi.update({ id, groupId: moveTo.trim() })).then(() => setMoveTo(""))}>Move</Button>
          <span className="flex-1" />
          <Button kind="danger" disabled={busy} onClick={() => { if (confirm("Delete this capture and its breadcrumbs?")) act(() => CaptureApi.delete(id)).then(() => navigate(href.captures())); }}>Delete</Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </Panel>

      {capture.error?.stack && (
        <Panel className="p-4">
          <h2 className="text-sm font-semibold mb-2">Stack</h2>
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words">{capture.error.stack}</pre>
        </Panel>
      )}

      <Panel className="p-4">
        <h2 className="text-sm font-semibold mb-2">Timeline <span className="text-neutral-400 font-normal">({capture.timeline.length} breadcrumbs, offsets from capture)</span></h2>
        <Timeline crumbs={capture.timeline} originIso={capture.timestamp} truncatedNote={truncatedNote} />
        <details className="mt-3">
          <summary className="text-xs text-neutral-500 cursor-pointer">Add breadcrumbs by hand</summary>
          <p className="text-xs text-neutral-500 mt-1">One per line, stored as an amendment timestamped now. Prefix with <code>category:</code> to set a category.</p>
          <textarea className={`${inputClass} w-full mt-1 font-mono`} rows={4} value={amendText} onChange={(e) => setAmendText(e.target.value)} />
          <Button
            disabled={busy || !amendText.trim()}
            onClick={() => {
              const now = new Date().toISOString();
              const breadcrumbs = amendText.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => {
                const m = line.match(/^([a-zA-Z0-9_.-]+):\s*(.+)$/);
                return { ts: now, seq: 100000 + i, message: m ? m[2] : line, category: m ? m[1] : "manual" };
              });
              act(() => CaptureApi.amend({ id, breadcrumbs })).then(() => setAmendText(""));
            }}
          >
            Add
          </Button>
        </details>
      </Panel>

      {capture.extra && Object.keys(capture.extra).length > 0 && (
        <Panel className="p-4">
          <h2 className="text-sm font-semibold mb-2">Extra</h2>
          <pre className="text-xs overflow-x-auto">{JSON.stringify(capture.extra, null, 2)}</pre>
        </Panel>
      )}
    </div>
  );
};
